import React, { useState, useEffect } from 'react'
import styles from './TourismPage.module.scss'
import { getTours, getTourCategories, bookTour, getMyTourBookings, getTourSchedules } from '../../../service/TourismService'
import { useAuth } from '../../../contexts/AuthContext'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import { MoneyIcon, CreditCardIcon, DiamondIcon, StarIcon, PeopleIcon, PhoneIcon, MountainIcon, MapIcon, DocumentIcon, InfoIcon, CalendarIcon, ClockIcon, WarningIcon, LocationIcon, GlobeIcon, RunningIcon, EmailIcon, CheckIcon } from 'components/CustomIcons'

const TourismPage = ({ embedded = false, goBack }) => {
  const { user, isAuthenticated } = useAuth()
  const [tours, setTours] = useState([])
  const [categories, setCategories] = useState([])
  const [myBookings, setMyBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [view, setView] = useState('tours') // tours, mybookings
  const [selectedTour, setSelectedTour] = useState(null)
  const [showTourModal, setShowTourModal] = useState(false)
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [tourSchedules, setTourSchedules] = useState([])
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    difficulty_level: '',
    duration_range: '',
    price_range: '',
    is_featured: false
  })
  const [sortBy, setSortBy] = useState('-is_featured')

  useEffect(() => {
    loadData()
  }, [currentPage, filters, sortBy, view])

  useEffect(() => {
    if (isAuthenticated) {
      loadMyBookings()
    }
  }, [isAuthenticated])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      await Promise.all([
        loadTours(),
        loadCategories()
      ])
    } catch (err) {
      setError(`Ошибка загрузки данных: ${err.message}`)
      setRetryAction(() => loadData)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadTours = async () => {
    const response = await getTours({
      page: currentPage,
      search: filters.search,
      category: filters.category,
      difficulty_level: filters.difficulty_level,
      duration_range: filters.duration_range,
      price_range: filters.price_range,
      is_featured: filters.is_featured || undefined,
      ordering: sortBy
    })
    setTours(response.results || response.data || [])
    setTotalPages(response.total_pages || 1)
  }

  const loadCategories = async () => {
    try {
      const response = await getTourCategories()
      setCategories(response.data || response || [])
    } catch (err) {
      console.error('Ошибка загрузки категорий:', err)
    }
  }

  const loadMyBookings = async () => {
    try {
      const response = await getMyTourBookings()
      setMyBookings(response.data || response || [])
    } catch (err) {
      console.error('Ошибка загрузки бронирований:', err)
    }
  }

  const loadTourSchedules = async (tourId) => {
    try {
      const response = await getTourSchedules(tourId)
      setTourSchedules(response.data || response || [])
    } catch (err) {
      console.error('Ошибка загрузки расписания:', err)
      setTourSchedules([])
    }
  }

  const handleTourClick = async (tour) => {
    setSelectedTour(tour)
    setShowTourModal(true)
  }

  const handleBookClick = async (tour) => {
    if (!isAuthenticated) {
      setError('Для бронирования тура необходимо авторизоваться')
      return
    }
    setSelectedTour(tour)
    await loadTourSchedules(tour.id)
    setShowBookingModal(true)
  }

  const handleBooking = async (e) => {
    e.preventDefault()
    
    const formData = new FormData(e.target)
    const scheduleId = formData.get('schedule_id')
    const participantsCount = formData.get('participants_count') || 1
    const contactPhone = formData.get('contact_phone')
    const contactEmail = formData.get('contact_email')
    const specialRequests = formData.get('special_requests') || ''
    
    if (!scheduleId) {
      setError('Пожалуйста, выберите дату тура')
      return
    }
    
    try {
      const bookingData = {
        schedule_id: parseInt(scheduleId),
        participants_count: parseInt(participantsCount),
        contact_phone: contactPhone,
        contact_email: contactEmail,
        special_requests: specialRequests
      }
      
      await bookTour(selectedTour.id, bookingData)
      setShowBookingModal(false)
      await loadMyBookings()
      
      // Показываем успешное уведомление
      setError(null)
      const notification = document.createElement('div')
      notification.className = 'booking-notification'
      notification.innerHTML = '✅ Тур успешно забронирован!'
      notification.style.cssText = `
        position: fixed;
        top: 90px;
        right: 20px;
        background: linear-gradient(135deg, #4CAF50, #45a049);
        color: white;
        padding: 16px 24px;
        border-radius: 12px;
        z-index: 9999;
        font-size: 16px;
        font-weight: 600;
        box-shadow: 0 8px 25px rgba(76, 175, 80, 0.4);
        animation: slideInRight 0.3s ease;
      `
      document.body.appendChild(notification)
      setTimeout(() => {
        notification.style.animation = 'slideOutRight 0.3s ease'
        setTimeout(() => notification.remove(), 300)
      }, 3000)
      
      // Переключаем на вкладку "Мои бронирования"
      setView('mybookings')
    } catch (err) {
      console.error('Ошибка бронирования:', err)
      setError(err.response?.data?.error || err.message || 'Ошибка бронирования. Попробуйте снова.')
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }

  const handleSearch = (term) => {
    setSearchTerm(term)
    setFilters(prev => ({ ...prev, search: term }))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const isBookedTour = (tourId) => {
    return myBookings.some(booking => booking.tour.id === tourId)
  }

  if (loading && tours.length === 0 && myBookings.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка туров...</p>
      </div>
    )
  }

  return (
    <div className={styles.tourismPage}>
      {!embedded && (
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            <span className={styles.icon}><MountainIcon size={32} /></span>
            Туризм и путешествия
          </h1>
          <p className={styles.pageSubtitle}>
            Откройте для себя удивительные места и приключения
          </p>
        </div>
      )}

      {/* Навигация между разделами */}
      <div className={styles.navigation}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${view === 'tours' ? styles.active : ''}`}
            onClick={() => setView('tours')}
          >
            Все туры
          </button>
          {isAuthenticated && (
            <button 
              className={`${styles.tab} ${view === 'mybookings' ? styles.active : ''}`}
              onClick={() => setView('mybookings')}
            >
              Мои бронирования ({myBookings.length})
            </button>
          )}
        </div>
      </div>

      {view === 'tours' && (
        <>
          {/* Поиск и фильтры */}
          <div className={styles.controls}>
            <div className={styles.topControls}>
              <input 
                type="text"
                placeholder="Поиск туров..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInput}
              />
              
              <div className={styles.sortSelect}>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className={styles.select}
                >
                  <option value="-is_featured">Рекомендуемые</option>
                  <option value="price_from">По цене (возр.)</option>
                  <option value="-price_from">По цене (убыв.)</option>
                  <option value="duration_days">По длительности</option>
                  <option value="difficulty_level">По сложности</option>
                  <option value="name">По названию</option>
                </select>
              </div>
            </div>

            <div className={styles.tourFilters}>
              <div className={styles.filterRow}>
                <select 
                  value={filters.category} 
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className={styles.select}
                >
                  <option value="">Все категории</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                
                <select 
                  value={filters.duration} 
                  onChange={(e) => handleFilterChange('duration', e.target.value)}
                  className={styles.select}
                >
                  <option value="">Любая длительность</option>
                  <option value="1">1 день</option>
                  <option value="3">3 дня</option>
                  <option value="7">1 неделя</option>
                </select>
                
                <select 
                  value={filters.price_range} 
                  onChange={(e) => handleFilterChange('price_range', e.target.value)}
                  className={styles.select}
                >
                  <option value="">Любая цена</option>
                  <option value="budget">Бюджетные</option>
                  <option value="mid-range">Средние</option>
                  <option value="luxury">Премиум</option>
                </select>
              </div>
            </div>
          </div>

          {error && (
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>
                <MountainIcon size={64} />
              </div>
              <div className={styles.errorContent}>
                <h3 className={styles.errorTitle}>Ошибка загрузки</h3>
                <p className={styles.errorMessage}>{error}</p>
                <button onClick={handleRetry} className={styles.retryButton} disabled={loading}>
                  {loading ? 'Загрузка...' : 'Повторить попытку'}
                </button>
              </div>
            </div>
          )}

          {/* Статистика */}
          <div className={styles.stats}>
            <span>Найдено туров: <strong>{tours.length}</strong></span>
            {isAuthenticated && (
              <span>Ваших бронирований: <strong>{myBookings.length}</strong></span>
            )}
          </div>

          {/* Сетка туров */}
          <div className={styles.toursGrid}>
            {tours.length === 0 && !loading ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <MountainIcon size={64} />
                </div>
                <h3>Туры не найдены</h3>
                <p>Попробуйте изменить фильтры поиска</p>
              </div>
            ) : (
              tours.map(tour => (
                <div key={tour.id} className={styles.tourCard} onClick={() => handleTourClick(tour)}>
                  <div className={styles.tourImage}>
                    <img 
                      src={tour.cover_image || tour.image || '/images/default-tour.jpg'} 
                      alt={tour.name}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMkEyQTJBIi8+CjxjaXJjbGUgY3g9IjE1MCIgY3k9IjEwMCIgcj0iNDAiIGZpbGw9IiMwMEJDRDQiIG9wYWNpdHk9IjAuNSIvPgo8dGV4dCB4PSIxNTAiIHk9IjEwNSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE4IiBmaWxsPSIjMDBCQ0Q0IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj7wn4+U77iPPC90ZXh0Pgo8L3N2Zz4K'
                      }}
                    />
                    <div className={styles.tourCategory}>
                      {tour.category_display || categories.find(cat => cat.id === tour.category)?.name || 'Тур'}
                    </div>
                    {tour.is_featured && (
                      <div className={styles.featuredBadge}>
                        <StarIcon size={14} filled={true} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} />
                        Рекомендуем
                      </div>
                    )}
                  </div>
                  <div className={styles.tourInfo}>
                    <h3>{tour.name}</h3>
                    <p className={styles.tourDescription}>{tour.description}</p>
                    <div className={styles.tourDetails}>
                      <div className={styles.duration}>
                        <span className={styles.durationIcon}><ClockIcon size={16} /></span>
                        {tour.duration_days} {tour.duration_days === 1 ? 'день' : 'дней'}
                      </div>
                      <div className={styles.destination}>
                        <span className={styles.destinationIcon}><GlobeIcon size={16} /></span>
                        {tour.route_description ? tour.route_description.split('\n')[0].substring(0, 30) + '...' : 'Кыргызстан'}
                      </div>
                      <div className={styles.difficulty}>
                        <span className={styles.difficultyIcon}><RunningIcon size={16} /></span>
                        {tour.difficulty_display}
                      </div>
                    </div>
                    <div className={styles.tourPrice}>
                      {tour.price_from ? `От ${tour.price_from} Сом` : 'По запросу'}
                    </div>
                    <div className={styles.tourActions}>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation()
                          handleTourClick(tour)
                        }}
                        className={styles.viewButton}
                      >
                        Подробнее
                      </button>
                      {isAuthenticated && !isBookedTour(tour.id) && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation()
                            handleBookClick(tour)
                          }}
                          className={styles.bookButton}
                        >
                          Забронировать
                        </button>
                      )}
                      {isBookedTour(tour.id) && (
                        <span className={styles.bookedBadge}>Забронирован</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {loading && (
            <div className={styles.loadingMore}>
              <Loader size="medium" />
            </div>
          )}

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {view === 'mybookings' && isAuthenticated && (
        <div className={styles.myBookings}>
          <h3>Мои бронирования туров</h3>
          {myBookings.length === 0 ? (
            <div className={styles.emptyState}>
              <p>Вы еще не забронировали ни одного тура.</p>
            </div>
          ) : (
            <div className={styles.bookingsList}>
              {myBookings.map(booking => (
                <div key={booking.id} className={styles.bookingCard}>
                  <h4>{booking.tour?.title || booking.tour?.name}</h4>
                  <p>Длительность: {booking.tour?.duration} дней</p>
                  <p>Дата: {booking.booking_date}</p>
                  <p>Статус: {booking.status}</p>
                  <p>Цена: {booking.total_price} Сом</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Модальные окна */}
      {showTourModal && selectedTour && (
        <div className={styles.tourModal} onClick={() => setShowTourModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <h3>{selectedTour.title || selectedTour.name}</h3>
                <div className={styles.modalBadges}>
                  {selectedTour.is_featured && (
                    <span className={styles.featuredBadge}>
                      <StarIcon size={14} filled={true} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} />
                      Рекомендуем
                    </span>
                  )}
                  <span className={styles.categoryBadge}>
                    {categories.find(cat => cat.id === selectedTour.category)?.name || 'Тур'}
                  </span>
                </div>
              </div>
              <button 
                className={styles.closeButton}
                onClick={() => setShowTourModal(false)}
                title="Закрыть"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              {/* Изображение тура */}
              {(selectedTour.cover_image || selectedTour.image) && (
                <div className={styles.tourModalImage}>
                  <img 
                    src={selectedTour.cover_image || selectedTour.image} 
                    alt={selectedTour.name}
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                </div>
              )}

              {/* Описание */}
              <div className={styles.tourSection}>
                <h4><DocumentIcon size={20} style={{ marginRight: '6px' }} /> Описание</h4>
                <p className={styles.tourFullDescription}>{selectedTour.description}</p>
              </div>

              {/* Основная информация */}
              <div className={styles.tourSection}>
                <h4><InfoIcon size={20} style={{ marginRight: '6px' }} /> Основная информация</h4>
                <div className={styles.tourInfoGrid}>
                  <div className={styles.infoCard}>
                    <div className={styles.infoIcon}><CalendarIcon size={24} /></div>
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Длительность</span>
                      <span className={styles.infoValue}>
                        {selectedTour.duration_days} {selectedTour.duration_days === 1 ? 'день' : 'дней'}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.infoCard}>
                    <div className={styles.infoIcon}><MapIcon size={24} /></div>
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Категория</span>
                      <span className={styles.infoValue}>{selectedTour.category_display}</span>
                    </div>
                  </div>
                  
                  <div className={styles.infoCard}>
                    <div className={styles.infoIcon}><RunningIcon size={24} /></div>
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Сложность</span>
                      <span className={styles.infoValue}>{selectedTour.difficulty_display}</span>
                    </div>
                  </div>
                  
                  <div className={styles.infoCard}>
                    <PeopleIcon className={styles.infoIcon} size={24} />
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Макс. группа</span>
                      <span className={styles.infoValue}>{selectedTour.max_group_size || 'Не ограничено'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Маршрут */}
              {selectedTour.route_description && (
                <div className={styles.tourSection}>
                  <h4>
                    <MapIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Маршрут
                  </h4>
                  <p className={styles.routeDescription}>{selectedTour.route_description}</p>
                </div>
              )}

              {/* Что включено */}
              {(selectedTour.included_services || selectedTour.includes) && (
                <div className={styles.tourSection}>
                  <h4>
                    <CheckIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle', color: '#4CAF50' }} />
                    Что включено
                  </h4>
                  <div className={styles.includesList}>
                    {(selectedTour.included_services || selectedTour.includes || '').split('\n').map((item, index) => 
                      item.trim() && (
                        <div key={index} className={styles.includeItem}>
                          <span className={styles.checkIcon}>✓</span>
                          <span>{item.trim()}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Что не включено */}
              {(selectedTour.excluded_services || selectedTour.excludes) && (
                <div className={styles.tourSection}>
                  <h4>
                    <WarningIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle', color: '#ff9800' }} />
                    Что не включено
                  </h4>
                  <div className={styles.excludesList}>
                    {(selectedTour.excluded_services || selectedTour.excludes || '').split('\n').map((item, index) => 
                      item.trim() && (
                        <div key={index} className={styles.excludeItem}>
                          <span className={styles.crossIcon}>×</span>
                          <span>{item.trim()}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Требования */}
              {selectedTour.requirements && (
                <div className={styles.tourSection}>
                  <h4>
                    <WarningIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle', color: '#ff9800' }} />
                    Требования
                  </h4>
                  <p className={styles.requirements}>{selectedTour.requirements}</p>
                </div>
              )}

              {/* Доступные даты и расписание */}
              {selectedTour.next_schedule && (
                <div className={styles.tourSection}>
                  <h4>
                    <CalendarIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Ближайшая дата
                  </h4>
                  <div className={styles.scheduleCard}>
                    <div className={styles.scheduleInfo}>
                      <div className={styles.scheduleItem}>
                        <span className={styles.scheduleIcon}>
                          <CalendarIcon size={18} />
                        </span>
                        <span>{new Date(selectedTour.next_schedule.date).toLocaleDateString('ru-RU', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}</span>
                      </div>
                      <div className={styles.scheduleItem}>
                        <span className={styles.scheduleIcon}>
                          <ClockIcon size={18} />
                        </span>
                        <span>Сбор в {selectedTour.next_schedule.meeting_time?.slice(0, 5) || 'уточняется'}</span>
                      </div>
                      <div className={styles.scheduleItem}>
                        <span className={styles.scheduleIcon}>
                          <PeopleIcon size={18} />
                        </span>
                        <span>Мест: {selectedTour.next_schedule.seats_available || 0}</span>
                      </div>
                    </div>
                    <div className={styles.schedulePrice}>
                      {parseInt(selectedTour.next_schedule.price).toLocaleString('ru-RU')} сом
                    </div>
                  </div>
                </div>
              )}

              {/* Цена и кнопка */}
              <div className={styles.tourModalFooter}>
                <div className={styles.priceSection}>
                  <span className={styles.priceLabel}>Цена от</span>
                  <span className={styles.priceAmount}>
                    {selectedTour.price_from ? `${parseInt(selectedTour.price_from).toLocaleString('ru-RU')} сом` : 'По запросу'}
                  </span>
                </div>
                {isAuthenticated && !isBookedTour(selectedTour.id) && (
                  <button 
                    onClick={() => {
                      setShowTourModal(false)
                      handleBookClick(selectedTour)
                    }}
                    className={styles.bookButton}
                  >
                    <span>📋</span>
                    Забронировать тур
                  </button>
                )}
                {isBookedTour(selectedTour.id) && (
                  <div className={styles.alreadyBooked}>
                    <span>✓</span>
                    Вы уже забронировали этот тур
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showBookingModal && selectedTour && (
        <div className={styles.tourBookingModal} onClick={() => setShowBookingModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <h3>
                  <MountainIcon size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Бронирование тура
                </h3>
                <p className={styles.tourName}>{selectedTour.name}</p>
              </div>
              <button 
                className={styles.closeButton}
                onClick={() => setShowBookingModal(false)}
                title="Закрыть"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              {/* Информация о туре */}
              <div className={styles.tourInfoBlock}>
                <div className={styles.tourInfoItem}>
                  <span className={styles.infoIcon}>
                    <CalendarIcon size={20} />
                  </span>
                  <div>
                    <strong>Длительность:</strong>
                    <p>{selectedTour.duration_days} {selectedTour.duration_days === 1 ? 'день' : 'дней'}</p>
                  </div>
                </div>
                <div className={styles.tourInfoItem}>
                  <span className={styles.infoIcon}>
                    <LocationIcon size={20} />
                  </span>
                  <div>
                    <strong>Сложность:</strong>
                    <p>{selectedTour.difficulty_display || selectedTour.difficulty_level}</p>
                  </div>
                </div>
                <div className={styles.tourInfoItem}>
                  <MoneyIcon className={styles.infoIcon} size={20} />
                  <div>
                    <strong>Цена от:</strong>
                    <p>{parseInt(selectedTour.price_from).toLocaleString('ru-RU')} сом</p>
                  </div>
                </div>
              </div>

              {error && (
                <div className={styles.errorBanner}>
                  <span className={styles.errorIcon}>
                    <WarningIcon size={18} />
                  </span>
                  <span>{error}</span>
                </div>
              )}
              
              <form onSubmit={handleBooking} className={styles.bookingForm}>
                <div className={styles.formGroup}>
                  <label htmlFor="schedule_id">
                    <span className={styles.labelIcon}>
                      <CalendarIcon size={18} />
                    </span>
                    Выберите дату тура *
                  </label>
                  {tourSchedules.length === 0 ? (
                    <div className={styles.noSchedules}>
                      <p>К сожалению, на данный момент нет доступных дат для этого тура.</p>
                      <p>Пожалуйста, свяжитесь с нами для уточнения информации.</p>
                    </div>
                  ) : (
                    <select 
                      id="schedule_id"
                      name="schedule_id" 
                      required 
                      className={styles.formControl}
                    >
                      <option value="">-- Выберите дату --</option>
                      {tourSchedules.map(schedule => (
                        <option 
                          key={schedule.id} 
                          value={schedule.id}
                          disabled={schedule.seats_available === 0}
                        >
                          {new Date(schedule.date).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                          {schedule.meeting_time && ` (${schedule.meeting_time.slice(0, 5)})`}
                          {' - '}
                          {schedule.seats_available > 0 
                            ? `${schedule.seats_available} ${schedule.seats_available === 1 ? 'место' : 'мест'} (${parseInt(schedule.price).toLocaleString('ru-RU')} сом)` 
                            : 'Мест нет'}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="participants_count">
                    <PeopleIcon className={styles.labelIcon} size={18} />
                    Количество участников *
                  </label>
                  <input 
                    id="participants_count"
                    type="number" 
                    name="participants_count"
                    min="1" 
                    max={tourSchedules[0]?.seats_available || 10}
                    defaultValue="1"
                    required 
                    className={styles.formControl}
                  />
                </div>
                
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="contact_phone">
                      <PhoneIcon className={styles.labelIcon} size={18} />
                      Телефон *
                    </label>
                    <input 
                      id="contact_phone"
                      type="tel" 
                      name="contact_phone"
                      placeholder="+996 XXX XXX XXX" 
                      defaultValue={user?.phone || ''}
                      required 
                      className={styles.formControl}
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="contact_email">
                      <span className={styles.labelIcon}>
                        <EmailIcon size={18} />
                      </span>
                      Email *
                    </label>
                    <input 
                      id="contact_email"
                      type="email" 
                      name="contact_email"
                      placeholder="your@email.com"
                      defaultValue={user?.email || ''}
                      required 
                      className={styles.formControl}
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="special_requests">
                    <span className={styles.labelIcon}>📝</span>
                    Особые пожелания
                  </label>
                  <textarea 
                    id="special_requests"
                    name="special_requests"
                    placeholder="Укажите дополнительные пожелания или требования (необязательно)..."
                    rows="4"
                    className={styles.formControl}
                  ></textarea>
                </div>
                
                <div className={styles.bookingFooter}>
                  <div className={styles.priceInfo}>
                    <span className={styles.priceLabel}>Цена за человека:</span>
                    <span className={styles.priceValue}>
                      {parseInt(selectedTour.price_from).toLocaleString('ru-RU')} сом
                    </span>
                  </div>
                  
                  <button 
                    type="submit" 
                    className={styles.submitButton}
                    disabled={tourSchedules.length === 0}
                  >
                    <span className={styles.buttonIcon}>✓</span>
                    Подтвердить бронирование
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TourismPage